import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/axios';
import { PaginationDefaults } from '../../../constants/defaults';
import { showSnackbar } from '../../../api/snackbarSlice';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';

const fetchWebhooks = async ({ queryKey }) => {
  const [, params] = queryKey;
  const { data } = await apiClient.get('/webhook-configs', { params });
  return data;
};

export const useGetWebhookHandler = (currentQuery = {}) => {
  const PageSize = PaginationDefaults.WEBHOOKS_LIMIT;
  const dispatch = useDispatch();

  const [page, setPage] = useState(0);
  const [webhooks, setWebhooks] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const queryParams = useMemo(
    () => ({
      ...currentQuery,
      page,
      page_size: PageSize,
    }),
    [currentQuery, page],
  );

  const {
    data: fetchedData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['webhooks', queryParams],
    queryFn: fetchWebhooks,
  });

  useEffect(() => {
    if (!fetchedData) return;

    const timer = setTimeout(() => {
      const { content = [], page_number, total_pages } = fetchedData;

      if (page_number === 0) {
        setWebhooks(content);
      } else {
        setWebhooks((prev) => {
          const existingIds = new Set(prev.map((w) => w.id));
          const newItems = content.filter((w) => !existingIds.has(w.id));
          return [...prev, ...newItems];
        });
      }

      setIsLoadingMore(false);
      setHasMore(page_number + 1 < total_pages);
    });

    return () => clearTimeout(timer);
  }, [fetchedData]);

  useEffect(() => {
    if (!isError) return;
    setIsLoadingMore(false);
    const message = error instanceof AxiosError ? error.response?.data?.message || error.message : 'An error occurred';
    dispatch(showSnackbar({ message, severity: 'error' }));
  }, [dispatch, error, isError]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return;
    setIsLoadingMore(true);
    setPage((prev) => prev + 1);
  }, [isLoadingMore, hasMore, isLoading]);

  const queryStr = JSON.stringify(currentQuery);
  useEffect(() => {
    setPage(0);
    setWebhooks([]);
  }, [queryStr]);

  return { webhooks, hasMore, isLoading: isLoading && page === 0, isLoadingMore, loadMore };
};
